import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import CityField from "../../components/CityField";
import {
  adminListOrganizations,
  adminGetOrganizationDetail,
  adminListOrganizationSupplierOptions,
} from "../../Admin/adminApi";
import "./AdminOrganizzazioni.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const EMPTY_ORG = {
  id: null,
  name: "",
  slug: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  is_active: true,
  supplier_id: "",
};

const EMPTY_ADDR = {
  id: null,
  label: "",
  street: "",
  cap: "",
  citta: "",
  provincia: "",
  phone: "",
  notes: "",
  active: true,
};

const EMPTY_SLOT = {
  id: null,
  day_of_week: "1",
  start_time: "",
  end_time: "",
  slot_type: "ritiro",
  is_active: true,
};

const DAY_OPTIONS = [
  { value: "0", label: "Domenica" },
  { value: "1", label: "Lunedì" },
  { value: "2", label: "Martedì" },
  { value: "3", label: "Mercoledì" },
  { value: "4", label: "Giovedì" },
  { value: "5", label: "Venerdì" },
  { value: "6", label: "Sabato" },
];

const SLOT_TYPE_OPTIONS = [
  { value: "ritiro", label: "Ritiro" },
  { value: "consegna", label: "Consegna" },
  { value: "entrambi", label: "Entrambi" },
];

function formatDbTimeToInput(v) {
  if (!v) return "";
  return String(v).slice(0, 5);
}

function formatSlotTime(start, end) {
  const a = formatDbTimeToInput(start);
  const b = formatDbTimeToInput(end);
  return a && b ? `${a} - ${b}` : "—";
}

function dayLabel(day) {
  return DAY_OPTIONS.find((d) => String(d.value) === String(day))?.label || "—";
}

function slotTypeLabel(type) {
  return SLOT_TYPE_OPTIONS.find((t) => t.value === type)?.label || "—";
}

function summarizeAddress(addr = {}) {
  return [
    addr.label || null,
    addr.street || null,
    [addr.cap, addr.citta].filter(Boolean).join(" ") || null,
    addr.provincia ? `(${addr.provincia})` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function fullName(person = {}) {
  return [person.name, person.last_name].filter(Boolean).join(" ").trim();
}

function initials(name = "") {
  return String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
}

function isDirty(base, current) {
  return JSON.stringify(base) !== JSON.stringify(current);
}

function groupSlotsByDay(slots = []) {
  const grouped = new Map();
  [...slots]
    .sort((a, b) => {
      const dayDiff = Number(a.day_of_week) - Number(b.day_of_week);
      if (dayDiff !== 0) return dayDiff;
      return String(a.start_time || "").localeCompare(String(b.start_time || ""));
    })
    .forEach((slot) => {
      const key = String(slot.day_of_week);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(slot);
    });
  return Array.from(grouped.entries()).map(([day, entries]) => ({
    day,
    label: dayLabel(day),
    entries,
  }));
}

export default function AdminOrganizzazioni() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgSearch, setOrgSearch] = useState("");

  const [orgForm, setOrgForm] = useState(EMPTY_ORG);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR);
  const [slotForm, setSlotForm] = useState(EMPTY_SLOT);
  const [organizationTimeSlots, setOrganizationTimeSlots] = useState([]);
  const [members, setMembers] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);

  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const debounceRef = useRef(null);

  const [cityConfirmed, setCityConfirmed] = useState(false);
  const [loadedSnapshot, setLoadedSnapshot] = useState({
    orgForm: EMPTY_ORG,
    addrForm: EMPTY_ADDR,
    members: [],
    slots: [],
  });

  const hasUnsavedChanges = useMemo(
    () =>
      isDirty(loadedSnapshot.orgForm, orgForm) ||
      isDirty(loadedSnapshot.addrForm, addrForm) ||
      isDirty(loadedSnapshot.members, members) ||
      isDirty(loadedSnapshot.slots, organizationTimeSlots),
    [loadedSnapshot, orgForm, addrForm, members, organizationTimeSlots]
  );

  const filteredOrganizations = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((org) => {
      const haystack = [
        org.name,
        org.slug,
        org.contact_name,
        org.contact_email,
        org.citta,
        org.provincia,
        org.supplier_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [organizations, orgSearch]);

  const selectedSupplier = useMemo(
    () =>
      supplierOptions.find((s) => String(s.id) === String(orgForm.supplier_id || "")) || null,
    [supplierOptions, orgForm.supplier_id]
  );

  const associatedUserIds = useMemo(
    () => new Set(members.map((m) => Number(m.user_id)).filter(Boolean)),
    [members]
  );

  const groupedSlots = useMemo(() => groupSlotsByDay(organizationTimeSlots), [organizationTimeSlots]);

  const summary = useMemo(() => {
    const activeMembersCount = members.filter((m) => String(m.status || "active") === "active").length;
    const hasHub = !!addrForm.street;
    const hasSupplier = !!orgForm.supplier_id;
    let setupStatus = "Bozza";
    if (!hasHub) setupStatus = "Hub mancante";
    else if (!hasSupplier) setupStatus = "Fornitore mancante";
    else if (!organizationTimeSlots.length) setupStatus = "Fasce da configurare";
    else if (!activeMembersCount) setupStatus = "Pronta senza membri";
    else setupStatus = "Configurata";

    return {
      name: orgForm.name || "Nuova organizzazione",
      status: orgForm.is_active ? "Attiva" : "Disattiva",
      hub: hasHub ? summarizeAddress(addrForm) : "Hub non configurato",
      supplier:
        selectedSupplier?.nome ||
        selectedSupplier?.nome_fornitore ||
        selectedSupplier?.name ||
        "Nessun fornitore associato",
      members: activeMembersCount,
      slotsCount: organizationTimeSlots.length,
      setupStatus,
    };
  }, [orgForm, addrForm, selectedSupplier, members, organizationTimeSlots]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const list = await adminListOrganizations(token);
      setOrganizations(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento organizzazioni");
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const list = await adminListOrganizationSupplierOptions(token);
      setSupplierOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento fornitori");
      setSupplierOptions([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const resetForNew = () => {
    setSelectedOrgId(null);
    setOrgForm(EMPTY_ORG);
    setAddrForm(EMPTY_ADDR);
    setMembers([]);
    setOrganizationTimeSlots([]);
    setSlotForm(EMPTY_SLOT);
    setClientQuery("");
    setClientResults([]);
    setCityConfirmed(false);
    setLoadedSnapshot({
      orgForm: EMPTY_ORG,
      addrForm: EMPTY_ADDR,
      members: [],
      slots: [],
    });
  };

  const loadOrganizationDetail = async (orgId) => {
    if (!orgId) {
      resetForNew();
      return;
    }

    try {
      const data = await adminGetOrganizationDetail(orgId, token);
      const nextOrg = {
        id: data.organization?.id ?? null,
        name: data.organization?.name || "",
        slug: data.organization?.slug || "",
        contact_name: data.organization?.contact_name || "",
        contact_email: data.organization?.contact_email || "",
        contact_phone: data.organization?.contact_phone || "",
        is_active: !!data.organization?.is_active,
        supplier_id:
          data.organization?.supplier_id != null ? String(data.organization.supplier_id) : "",
      };

      const firstAddress = Array.isArray(data.addresses) ? data.addresses[0] : null;
      const nextAddr = {
        id: firstAddress?.id ?? null,
        label: firstAddress?.label || "",
        street: firstAddress?.street || "",
        cap: firstAddress?.cap || "",
        citta: firstAddress?.citta || "",
        provincia: firstAddress?.provincia || "",
        phone: firstAddress?.phone || "",
        notes: firstAddress?.notes || "",
        active: firstAddress?.active ?? true,
      };

      const nextMembers = Array.isArray(data.members) ? data.members : [];
      const nextSlots = Array.isArray(data.organization_supplier_time_slots)
        ? data.organization_supplier_time_slots
        : Array.isArray(data.organization_time_slots)
          ? data.organization_time_slots
          : [];

      setOrgForm(nextOrg);
      setAddrForm(nextAddr);
      setMembers(nextMembers);
      setOrganizationTimeSlots(nextSlots);
      setSlotForm(EMPTY_SLOT);
      setClientQuery("");
      setClientResults([]);
      setCityConfirmed(!!nextAddr.citta);
      setLoadedSnapshot({
        orgForm: nextOrg,
        addrForm: nextAddr,
        members: nextMembers,
        slots: nextSlots,
      });
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento dettaglio organizzazione");
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadSuppliers();
    resetForNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmAbandonChanges = () => {
    if (!hasUnsavedChanges) return true;
    return window.confirm("Ci sono modifiche non salvate. Vuoi davvero cambiare organizzazione?");
  };

  const handleSelectOrganization = (orgId) => {
    if (selectedOrgId === orgId) return;
    if (!confirmAbandonChanges()) return;
    setSelectedOrgId(orgId);
    loadOrganizationDetail(orgId);
  };

  const handleNewOrganization = () => {
    if (!confirmAbandonChanges()) return;
    resetForNew();
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = clientQuery.trim();
    if (q.length < 2) {
      setClientResults([]);
      setSearchingClients(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setSearchingClients(true);
        const res = await fetch(
          `${API_BASE}/api/admin/organizations/customers/search?q=${encodeURIComponent(q)}&limit=8`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error(data?.error || "Errore ricerca clienti");
        setClientResults(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setClientResults([]);
      } finally {
        setSearchingClients(false);
      }
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [clientQuery, token]);

  const addMemberLocal = (user) => {
    const userId = Number(user?.id || user?.user_id);
    if (!userId || associatedUserIds.has(userId)) return;

    setMembers((prev) => [
      ...prev,
      {
        user_id: userId,
        name: user.name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone: user.phone || "",
        codice_fiscale: user.codice_fiscale || "",
        role: "member",
        status: "active",
      },
    ]);
    setClientQuery("");
    setClientResults([]);
  };

  const removeMemberLocal = (userId) => {
    setMembers((prev) => prev.filter((m) => Number(m.user_id) !== Number(userId)));
  };

  const startEditSlot = (slot) => {
    setSlotForm({
      id: slot.id ?? null,
      day_of_week: String(slot.day_of_week ?? "1"),
      start_time: formatDbTimeToInput(slot.start_time),
      end_time: formatDbTimeToInput(slot.end_time),
      slot_type: slot.slot_type || "ritiro",
      is_active: slot.is_active ?? true,
    });
  };

  const resetSlotForm = () => setSlotForm(EMPTY_SLOT);

  const saveSlotLocal = (e) => {
    e.preventDefault();

    if (!orgForm.supplier_id) {
      toast.error("Seleziona prima il fornitore associato");
      return;
    }
    if (!slotForm.start_time || !slotForm.end_time) {
      toast.error("Compila ora inizio e ora fine");
      return;
    }
    if (slotForm.start_time >= slotForm.end_time) {
      toast.error("L'ora inizio deve essere precedente a ora fine");
      return;
    }

    const nextSlot = {
      id: slotForm.id || `tmp-${Date.now()}`,
      day_of_week: Number(slotForm.day_of_week),
      start_time: `${slotForm.start_time}:00`.slice(0, 8),
      end_time: `${slotForm.end_time}:00`.slice(0, 8),
      slot_type: slotForm.slot_type,
      is_active: true,
    };

    const overlap = organizationTimeSlots.some((slot) => {
      if (String(slot.id) === String(nextSlot.id)) return false;
      const sameDay = Number(slot.day_of_week) === Number(nextSlot.day_of_week);
      const sameType =
        slot.slot_type === nextSlot.slot_type ||
        slot.slot_type === "entrambi" ||
        nextSlot.slot_type === "entrambi";
      const slotStart = formatDbTimeToInput(slot.start_time);
      const slotEnd = formatDbTimeToInput(slot.end_time);
      const nextStart = formatDbTimeToInput(nextSlot.start_time);
      const nextEnd = formatDbTimeToInput(nextSlot.end_time);
      const noOverlap = nextEnd <= slotStart || nextStart >= slotEnd;
      return sameDay && sameType && !noOverlap;
    });

    if (overlap) {
      toast.error("Esiste già una fascia sovrapposta per questo giorno/tipo");
      return;
    }

    setOrganizationTimeSlots((prev) => {
      const withoutCurrent = prev.filter((slot) => String(slot.id) !== String(nextSlot.id));
      return [...withoutCurrent, nextSlot].sort((a, b) => {
        const dayDiff = Number(a.day_of_week) - Number(b.day_of_week);
        if (dayDiff !== 0) return dayDiff;
        return String(a.start_time || "").localeCompare(String(b.start_time || ""));
      });
    });
    setSlotForm(EMPTY_SLOT);
  };

  const removeSlotLocal = (slotId) => {
    setOrganizationTimeSlots((prev) => prev.filter((slot) => String(slot.id) !== String(slotId)));
    if (String(slotForm.id) === String(slotId)) setSlotForm(EMPTY_SLOT);
  };

  const saveAll = async () => {
    if (!orgForm.name.trim()) {
      toast.error("Nome organizzazione obbligatorio");
      return;
    }
    if (!addrForm.street.trim()) {
      toast.error("Indirizzo hub obbligatorio");
      return;
    }
    if (!addrForm.citta.trim()) {
      toast.error("Città obbligatoria");
      return;
    }
    if (!cityConfirmed) {
      toast.error("Seleziona la città dall'elenco suggerito");
      return;
    }
    if (!orgForm.supplier_id) {
      toast.error("Seleziona un fornitore associato");
      return;
    }

    try {
      setSavingAll(true);
      const payload = {
        id: orgForm.id || null,
        name: orgForm.name,
        slug: orgForm.slug,
        contact_name: orgForm.contact_name,
        contact_email: orgForm.contact_email,
        contact_phone: orgForm.contact_phone,
        is_active: !!orgForm.is_active,
        supplier_id: Number(orgForm.supplier_id),
        address: {
          ...addrForm,
          active: !!addrForm.active,
        },
        members: members.map((m) => ({
          user_id: Number(m.user_id),
          role: String(m.role || "member"),
          status: String(m.status || "active"),
        })),
        time_slots: organizationTimeSlots.map((slot) => ({
          id: slot.id,
          day_of_week: Number(slot.day_of_week),
          start_time: formatDbTimeToInput(slot.start_time),
          end_time: formatDbTimeToInput(slot.end_time),
          slot_type: slot.slot_type,
          is_active: !!slot.is_active,
        })),
      };

      const res = await fetch(`${API_BASE}/api/admin/organizations/save-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Errore salvataggio organizzazione");

      toast.success(orgForm.id ? "Organizzazione aggiornata" : "Organizzazione creata");
      await loadOrganizations();
      const nextId = data?.organization_id || data?.organization?.id || orgForm.id;
      setSelectedOrgId(nextId || null);
      if (nextId) {
        await loadOrganizationDetail(nextId);
      } else {
        resetForNew();
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Errore salvataggio organizzazione");
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="org-page">
      <div className="org-page__head">
        <div>
          <h1>Organizzazioni</h1>
          <p>Configura da un solo pannello anagrafica, hub, fornitore dedicato, fasce e clienti associati.</p>
        </div>
        <div className="org-head-actions">
          {hasUnsavedChanges && <span className="org-badge is-warning">Modifiche non salvate</span>}
          <button className="org-btn org-btn--primary" onClick={handleNewOrganization}>
            + Nuova organizzazione
          </button>
        </div>
      </div>

      <div className="org-layout">
        <aside className="org-sidebar">
          <div className="org-card">
            <div className="org-card__head org-card__head--tight">
              <div>
                <h2>Organizzazioni</h2>
                <p className="org-card__desc">Cerca e apri una configurazione esistente.</p>
              </div>
              <span className="org-badge is-info">{organizations.length}</span>
            </div>

            <div className="org-search org-search--sidebar">
              <input
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                placeholder="Cerca nome, città, fornitore…"
              />
            </div>

            <ul className="org-list">
              <li>
                <button
                  className={`org-list__item ${selectedOrgId == null ? "is-active" : ""}`}
                  onClick={handleNewOrganization}
                >
                  <div className="org-list__top">
                    <div className="org-list__avatar">+</div>
                    <div className="org-list__identity">
                      <div className="org-list__title">Nuova organizzazione</div>
                      <div className="org-list__meta">Apri il form vuoto e configura tutto in un solo salvataggio.</div>
                    </div>
                  </div>
                </button>
              </li>

              {filteredOrganizations.map((org) => {
                const configured = !!org.supplier_id;
                return (
                  <li key={org.id}>
                    <button
                      className={`org-list__item ${selectedOrgId === org.id ? "is-active" : ""}`}
                      onClick={() => handleSelectOrganization(org.id)}
                    >
                      <div className="org-list__top">
                        <div className="org-list__avatar">{initials(org.name || "O")}</div>
                        <div className="org-list__identity">
                          <div className="org-list__title">{org.name}</div>
                          <div className="org-list__meta">
                            {[org.citta, org.provincia && `(${org.provincia})`].filter(Boolean).join(" ") || "Località non impostata"}
                          </div>
                        </div>
                      </div>
                      <div className="org-list__badges">
                        <span className={`org-badge ${org.is_active ? "is-success" : "is-muted"}`}>
                          {org.is_active ? "Attiva" : "Disattiva"}
                        </span>
                        <span className={`org-badge ${configured ? "is-info" : "is-warning"}`}>
                          {configured ? "Fornitore ok" : "Da configurare"}
                        </span>
                      </div>
                      <div className="org-list__stats">
                        <span>{org.members_count || 0} membri</span>
                        <span>•</span>
                        <span>{org.supplier_name || "Nessun fornitore"}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {!loading && filteredOrganizations.length === 0 && (
              <div className="org-empty-box">Nessuna organizzazione trovata con questo filtro.</div>
            )}
          </div>
        </aside>

        <section className="org-main">
          <div className="org-card org-card--overview">
            <div className="org-card__head">
              <div>
                <h2>{orgForm.id ? "Panoramica organizzazione" : "Nuova organizzazione"}</h2>
                <p className="org-card__desc">Compila tutto e salva una volta sola.</p>
              </div>
              <span className={`org-badge ${summary.setupStatus === "Configurata" ? "is-success" : "is-warning"}`}>
                {summary.setupStatus}
              </span>
            </div>
            <div className="org-overview-grid">
              <div className="org-overview-tile">
                <span className="org-overview-label">Organizzazione</span>
                <strong>{summary.name}</strong>
                <small>{summary.status}</small>
              </div>
              <div className="org-overview-tile">
                <span className="org-overview-label">Hub</span>
                <strong>{summary.hub}</strong>
                <small>Indirizzo operativo usato dai membri</small>
              </div>
              <div className="org-overview-tile">
                <span className="org-overview-label">Fornitore</span>
                <strong>{summary.supplier}</strong>
                <small>{summary.slotsCount} fasce dedicate</small>
              </div>
              <div className="org-overview-tile">
                <span className="org-overview-label">Clienti</span>
                <strong>{summary.members}</strong>
                <small>Utenti attivi collegati</small>
              </div>
            </div>
          </div>

          <div className="org-grid-layout">
            <div className="org-stack">
              <div className="org-card">
                <div className="org-card__head">
                  <div>
                    <h2>1. Anagrafica organizzazione</h2>
                    <p className="org-card__desc">Dati base e stato dell'organizzazione.</p>
                  </div>
                </div>

                <div className="org-grid">
                  <label>
                    <span>Nome</span>
                    <input
                      value={orgForm.name}
                      onChange={(e) => setOrgForm((s) => ({ ...s, name: e.target.value }))}
                    />
                  </label>

                  <label>
                    <span>Slug</span>
                    <input
                      value={orgForm.slug}
                      onChange={(e) => setOrgForm((s) => ({ ...s, slug: e.target.value }))}
                    />
                  </label>

                  <label>
                    <span>Referente</span>
                    <input
                      value={orgForm.contact_name}
                      onChange={(e) => setOrgForm((s) => ({ ...s, contact_name: e.target.value }))}
                    />
                  </label>

                  <label>
                    <span>Email referente</span>
                    <input
                      value={orgForm.contact_email}
                      onChange={(e) => setOrgForm((s) => ({ ...s, contact_email: e.target.value }))}
                    />
                  </label>

                  <label>
                    <span>Telefono referente</span>
                    <input
                      value={orgForm.contact_phone}
                      onChange={(e) => setOrgForm((s) => ({ ...s, contact_phone: e.target.value }))}
                    />
                  </label>

                  <label className="org-check">
                    <input
                      type="checkbox"
                      checked={!!orgForm.is_active}
                      onChange={(e) => setOrgForm((s) => ({ ...s, is_active: e.target.checked }))}
                    />
                    <span>Organizzazione attiva</span>
                  </label>
                </div>
              </div>

              <div className="org-card">
                <div className="org-card__head">
                  <div>
                    <h2>2. Hub organizzazione</h2>
                    <p className="org-card__desc">L'indirizzo condiviso che i membri useranno nel flusso dedicato.</p>
                  </div>
                </div>

                <div className="org-grid">
                  <label>
                    <span>Etichetta</span>
                    <input
                      value={addrForm.label}
                      onChange={(e) => setAddrForm((s) => ({ ...s, label: e.target.value }))}
                      placeholder="Es. Sede principale"
                    />
                  </label>

                  <label>
                    <span>Telefono hub</span>
                    <input
                      value={addrForm.phone}
                      onChange={(e) => setAddrForm((s) => ({ ...s, phone: e.target.value }))}
                    />
                  </label>

                  <label className="org-grid__full">
                    <span>Indirizzo</span>
                    <input
                      value={addrForm.street}
                      onChange={(e) => setAddrForm((s) => ({ ...s, street: e.target.value }))}
                      placeholder="Via e civico"
                    />
                  </label>

                  <div className="org-grid__full org-city-field-wrap">
                    <CityField
                      label="Città"
                      value={addrForm.citta}
                      provinciaValue={addrForm.provincia}
                      capValue={addrForm.cap}
                      hardConfirm
                      confirmed={cityConfirmed}
                      setConfirmed={setCityConfirmed}
                      resetProvinciaOnType
                      resetCapOnType
                      onChangeCity={(v, meta) => {
                        setAddrForm((s) => ({
                          ...s,
                          citta: v,
                          provincia: meta?.resetProvincia ? "" : s.provincia,
                          cap: meta?.resetCap ? "" : s.cap,
                        }));
                      }}
                      onApplyCity={({ name, provincia, cap }) => {
                        setAddrForm((s) => ({
                          ...s,
                          citta: name || "",
                          provincia: (provincia || "").toUpperCase(),
                          cap: cap || "",
                        }));
                      }}
                      inputClassName={!cityConfirmed && addrForm.citta ? "input-invalid" : ""}
                      showEmptyHint
                    />
                  </div>

                  <label>
                    <span>Provincia</span>
                    <input value={addrForm.provincia} readOnly placeholder="es. MO" />
                  </label>

                  <label>
                    <span>CAP</span>
                    <input
                      value={addrForm.cap}
                      onChange={(e) =>
                        setAddrForm((s) => ({
                          ...s,
                          cap: e.target.value.replace(/\D/g, "").slice(0, 5),
                        }))
                      }
                      inputMode="numeric"
                    />
                  </label>

                  <label className="org-grid__full">
                    <span>Note</span>
                    <input
                      value={addrForm.notes}
                      onChange={(e) => setAddrForm((s) => ({ ...s, notes: e.target.value }))}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="org-stack">
              <div className="org-card">
                <div className="org-card__head">
                  <div>
                    <h2>3. Fornitore e fasce dedicate</h2>
                    <p className="org-card__desc">Associa il fornitore e configura subito le fasce settimanali riservate all'organizzazione.</p>
                  </div>
                </div>

                <div className="org-grid org-grid--service-config">
                  <label className="org-grid__full">
                    <span>Fornitore associato</span>
                    <select
                      value={orgForm.supplier_id}
                      onChange={(e) => setOrgForm((s) => ({ ...s, supplier_id: e.target.value }))}
                      disabled={loadingSuppliers}
                    >
                      <option value="">-- Seleziona fornitore --</option>
                      {supplierOptions.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.nome_fornitore || supplier.nome || supplier.name || `Fornitore #${supplier.id}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {!!orgForm.supplier_id && (
                  <>
                    <div className="org-inline-note">
                      Fornitore attuale: <strong>{selectedSupplier?.nome_fornitore || selectedSupplier?.nome || selectedSupplier?.name || `#${orgForm.supplier_id}`}</strong>
                    </div>

                    <form className="org-form" onSubmit={saveSlotLocal}>
                      <div className="org-grid">
                        <label>
                          <span>Giorno settimana</span>
                          <select
                            value={slotForm.day_of_week}
                            onChange={(e) => setSlotForm((s) => ({ ...s, day_of_week: e.target.value }))}
                          >
                            {DAY_OPTIONS.map((day) => (
                              <option key={day.value} value={day.value}>{day.label}</option>
                            ))}
                          </select>
                        </label>

                        <label>
                          <span>Tipo</span>
                          <select
                            value={slotForm.slot_type}
                            onChange={(e) => setSlotForm((s) => ({ ...s, slot_type: e.target.value }))}
                          >
                            {SLOT_TYPE_OPTIONS.map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </label>

                        <label>
                          <span>Ora inizio</span>
                          <input
                            type="time"
                            value={slotForm.start_time}
                            onChange={(e) => setSlotForm((s) => ({ ...s, start_time: e.target.value }))}
                          />
                        </label>

                        <label>
                          <span>Ora fine</span>
                          <input
                            type="time"
                            value={slotForm.end_time}
                            onChange={(e) => setSlotForm((s) => ({ ...s, end_time: e.target.value }))}
                          />
                        </label>
                      </div>

                      <div className="org-actions org-actions--between">
                        <button type="button" className="org-btn" onClick={resetSlotForm}>
                          {slotForm.id ? "Annulla modifica" : "Reset"}
                        </button>
                        <button type="submit" className="org-btn org-btn--primary">
                          {slotForm.id ? "Aggiorna fascia" : "Aggiungi fascia"}
                        </button>
                      </div>
                    </form>

                    <div className="org-slots-grouped">
                      {groupedSlots.length === 0 ? (
                        <div className="org-empty-box">Nessuna fascia configurata.</div>
                      ) : (
                        groupedSlots.map((group) => (
                          <div key={group.day} className="org-slots-day">
                            <div className="org-slots-day__title">{group.label}</div>
                            <div className="org-slots">
                              {group.entries.map((slot) => (
                                <div key={slot.id} className="org-slot-row">
                                  <div className="org-slot-main">
                                    <div className="org-slot-title">
                                      {formatSlotTime(slot.start_time, slot.end_time)}
                                    </div>
                                    <div className="org-slot-meta">{slotTypeLabel(slot.slot_type)}</div>
                                  </div>
                                  <div className="org-slot-actions">
                                    <button type="button" className="org-btn" onClick={() => startEditSlot(slot)}>
                                      Modifica
                                    </button>
                                    <button type="button" className="org-btn org-btn--danger" onClick={() => removeSlotLocal(slot.id)}>
                                      Rimuovi
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}

                {!orgForm.supplier_id && (
                  <div className="org-empty-box">Seleziona un fornitore per iniziare a configurare le fasce dedicate.</div>
                )}
              </div>

              <div className="org-card">
                <div className="org-card__head">
                  <div>
                    <h2>4. Clienti associati</h2>
                    <p className="org-card__desc">Cerca in autocomplete e aggiungi i clienti che devono usare questo hub.</p>
                  </div>
                  <span className="org-badge is-info">{members.length}</span>
                </div>

                <div className="org-autocomplete">
                  <input
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    placeholder="Cerca per nome, email o CF"
                  />
                  {(searchingClients || clientResults.length > 0) && clientQuery.trim().length >= 2 && (
                    <div className="org-autocomplete__menu">
                      {searchingClients && <div className="org-autocomplete__item is-muted">Cerco…</div>}
                      {!searchingClients && clientResults.length === 0 && (
                        <div className="org-autocomplete__item is-muted">Nessun risultato</div>
                      )}
                      {!searchingClients &&
                        clientResults.map((user) => {
                          const already = associatedUserIds.has(Number(user.id));
                          return (
                            <button
                              type="button"
                              key={user.id}
                              className="org-autocomplete__item"
                              onClick={() => !already && addMemberLocal(user)}
                              disabled={already}
                            >
                              <div>
                                <div className="org-result-name">{fullName(user) || user.email || `Utente #${user.id}`}</div>
                                <div className="org-result-meta">
                                  {[user.email, user.codice_fiscale].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                              {already ? <span className="org-badge is-muted">Già associato</span> : <span className="org-badge is-success">Aggiungi</span>}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                {members.length === 0 ? (
                  <div className="org-empty-box">Nessun cliente associato.</div>
                ) : (
                  <div className="org-members">
                    {members.map((m) => (
                      <div key={m.user_id} className="org-member-row">
                        <div>
                          <div className="org-result-name">{fullName(m) || m.email || `Utente #${m.user_id}`}</div>
                          <div className="org-result-meta">
                            {[m.email, m.codice_fiscale, m.status || "active"].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <button className="org-btn org-btn--danger" type="button" onClick={() => removeMemberLocal(m.user_id)}>
                          Rimuovi
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="org-page__footerActions">
            <button className="org-btn" type="button" onClick={handleNewOrganization} disabled={savingAll}>
              Annulla / Nuova
            </button>
            <button className="org-btn org-btn--primary org-btn--lg" type="button" onClick={saveAll} disabled={savingAll}>
              {savingAll ? "Salvataggio…" : "Salva tutto"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
